import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import { BrandMark } from "../../brand/BrandMark";
import { formatDateTime, formatMoney, zonedInputToISO } from "../../lib/format";
import type { TenantBrand } from "../../theme/tenantBrand";
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { SkeletonCard, SkeletonPageHeader } from "../../ui/skeletons";

interface PublicTenant {
  slug: string;
  displayName: string;
  locale: string;
  currency: string;
  timezone: string;
  brand: TenantBrand;
}
interface Question {
  key: string;
  label: string;
  kind: "text" | "textarea" | "choice" | "boolean";
  required: boolean;
  options?: string[];
}
interface Offering {
  configurationId: string;
  configurationVersion: number;
  venueId: string;
  venueName: string;
  locationId: string;
  locationName: string;
  kind: string;
  capacity?: number;
  accessibility: Record<string, unknown>;
  facilities: Record<string, unknown>;
  timezone: string;
  mode: string;
  details: {
    headline: string;
    description: string;
    minimumDurationMinutes: number;
    maximumDurationMinutes: number;
    slotIntervalMinutes: number;
    holdMinutes: number;
    minimumLeadMinutes: number;
    maximumAdvanceDays: number;
    allowWithoutOpeningHours: boolean;
    requirePayment: boolean;
    depositBasisPoints: number;
    questions: Question[];
  };
  privacyNoticeVersion: string;
  termsVersion: string;
  cancellationPolicyVersion: string;
  fromAmountMinor?: number;
  currency: string;
}
interface Surface {
  tenant: PublicTenant;
  offerings: Offering[];
}
interface Slot {
  start: string;
  end: string;
  amountMinor?: number;
  currency: string;
}
interface HoldResult {
  requestId: string;
  requestToken?: string;
  state: string;
  expiresAt: string;
  start: string;
  end: string;
  amountMinor?: number;
  currency: string;
  replayed: boolean;
}
interface SubmissionResult {
  requestId: string;
  bookingId: string;
  bookingReference: string;
  state: string;
  bookingStatus: string;
  paymentRequired: boolean;
  paymentUrl?: string;
  paymentExpiresAt?: string;
}
interface RequestStatus {
  requestId: string;
  state: string;
  expiresAt?: string;
  bookingId?: string;
  bookingReference?: string;
  bookingStatus?: string;
  paymentRequired: boolean;
  paymentUrl?: string;
  paymentExpiresAt?: string;
}

export function PublicBookingPage() {
  const { tenantSlug = "" } = useParams();
  const surface = useQuery({
    queryKey: ["public-booking", tenantSlug],
    queryFn: () => api<Surface>(`/api/v1/book/${encodeURIComponent(tenantSlug)}`),
    retry: false,
  });
  const [offering, setOffering] = useState<Offering | null>(null);
  const [date, setDate] = useState(() =>
    new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
  );
  const [duration, setDuration] = useState(60);
  const [attendees, setAttendees] = useState(1);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [hold, setHold] = useState<HoldResult | null>(null);
  const [requestToken, setRequestToken] = useState("");
  const [result, setResult] = useState<SubmissionResult | RequestStatus | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const storageKey = `venueops-public-request:${tenantSlug}`;

  useEffect(() => {
    if (!surface.data) return;
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return;
    try {
      const stored = JSON.parse(raw) as { token: string; configurationId: string };
      const savedOffering = surface.data.offerings.find(
        (item) => item.configurationId === stored.configurationId,
      );
      if (!stored.token || !savedOffering) return;
      setOffering(savedOffering);
      setRequestToken(stored.token);
      void api<RequestStatus>(`/api/v1/book/${encodeURIComponent(tenantSlug)}/requests/status`, {
        token: stored.token,
      })
        .then(setResult)
        .catch(() => sessionStorage.removeItem(storageKey));
    } catch {
      sessionStorage.removeItem(storageKey);
    }
  }, [storageKey, surface.data, tenantSlug]);

  function chooseOffering(item: Offering) {
    setOffering(item);
    setDuration(item.details.minimumDurationMinutes);
    setAttendees(1);
    setSlots([]);
    setHold(null);
    setRequestToken("");
    setResult(null);
    setError(undefined);
    sessionStorage.removeItem(storageKey);
  }

  async function findAvailability(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!offering) return;
    setWorking(true);
    setError(undefined);
    setSlots([]);
    try {
      const calendarDate = new Date(`${date}T12:00:00Z`);
      calendarDate.setUTCDate(calendarDate.getUTCDate() + 7);
      const endDate = calendarDate.toISOString().slice(0, 10);
      const from = zonedInputToISO(`${date}T00:00`, offering.timezone);
      const to = zonedInputToISO(`${endDate}T00:00`, offering.timezone);
      const response = await api<{ items: Slot[] }>(
        `/api/v1/book/${encodeURIComponent(tenantSlug)}/availability`,
        {
          method: "POST",
          body: jsonBody({
            configurationId: offering.configurationId,
            from,
            to,
            durationMinutes: duration,
            attendeeCount: attendees,
          }),
        },
      );
      setSlots(response.items);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createHold(slot: Slot) {
    if (!offering) return;
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<HoldResult>(`/api/v1/book/${encodeURIComponent(tenantSlug)}/holds`, {
        method: "POST",
        body: jsonBody({
          configurationId: offering.configurationId,
          start: slot.start,
          end: slot.end,
          idempotencyKey: crypto.randomUUID(),
          attendeeCount: attendees,
        }),
      });
      if (!item.requestToken)
        throw new Error("We couldn’t reserve that time. Please choose it again.");
      setHold(item);
      setRequestToken(item.requestToken);
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ token: item.requestToken, configurationId: offering.configurationId }),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!offering || !requestToken) return;
    const form = new FormData(event.currentTarget);
    const answers: Record<string, string | boolean> = {};
    for (const question of offering.details.questions)
      answers[question.key] =
        question.kind === "boolean"
          ? form.get(question.key) === "on"
          : String(form.get(question.key) ?? "");
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<SubmissionResult>(
        `/api/v1/book/${encodeURIComponent(tenantSlug)}/requests/submit`,
        {
          method: "POST",
          token: requestToken,
          body: jsonBody({
            contactName: String(form.get("contactName")),
            contactEmail: String(form.get("contactEmail")),
            contactPhone: String(form.get("contactPhone")),
            title: String(form.get("title")),
            attendeeCount: attendees,
            answers,
            privacyNoticeVersion: offering.privacyNoticeVersion,
            termsVersion: offering.termsVersion,
            cancellationPolicyVersion: offering.cancellationPolicyVersion,
            marketingEmailConsent: form.get("marketingEmailConsent") === "on",
          }),
        },
      );
      setResult(item);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function retryPayment() {
    if (!requestToken) return;
    setWorking(true);
    setError(undefined);
    try {
      setResult(
        await api<SubmissionResult>(
          `/api/v1/book/${encodeURIComponent(tenantSlug)}/requests/payment`,
          { method: "POST", token: requestToken, body: jsonBody({}) },
        ),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (surface.isLoading)
    return (
      <div className="public-main">
        <SkeletonPageHeader />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  if (surface.error)
    return (
      <div className="public-main">
        <ErrorPanel error={surface.error} />
      </div>
    );
  if (!surface.data) return null;
  const step =
    result && result.state !== "held" ? 4 : requestToken ? 3 : slots.length ? 2 : offering ? 1 : 0;

  return (
    <div className="public-layout">
      <header className="public-header">
        {surface.data.tenant.brand.logoUrl ? (
          <img className="brand-logo" src={surface.data.tenant.brand.logoUrl} alt="" />
        ) : (
          <BrandMark />
        )}
        <div>
          <strong>{surface.data.tenant.displayName}</strong>
          <small>Venue booking</small>
        </div>
      </header>
      <main className="public-main">
        <div
          className="steps"
          role="progressbar"
          aria-label="Booking progress"
          aria-valuemin={1}
          aria-valuemax={5}
          aria-valuenow={step + 1}
        >
          {[0, 1, 2, 3, 4].map((item) => (
            <span key={item} className={`step ${item <= step ? "active" : ""}`} />
          ))}
        </div>
        {error ? <ErrorPanel error={error} /> : null}
        {!offering ? (
          <>
            <header className="page-header">
              <div>
                <h1>Find the right space</h1>
                <p>Choose a space and we’ll show the times currently available.</p>
              </div>
            </header>
            {surface.data.offerings.length ? (
              <div className="offering-grid">
                {surface.data.offerings.map((item) => (
                  <Card className="offering" key={item.configurationId}>
                    <small>{item.venueName}</small>
                    <h2>{item.details.headline}</h2>
                    <p>{item.details.description || `${item.locationName} · ${item.kind}`}</p>
                    <dl className="definition-list">
                      <dt>Location</dt>
                      <dd>{item.locationName}</dd>
                      <dt>Capacity</dt>
                      <dd>{item.capacity ?? "Ask venue"}</dd>
                      <dt>Duration</dt>
                      <dd>
                        {item.details.minimumDurationMinutes}–{item.details.maximumDurationMinutes}{" "}
                        minutes
                      </dd>
                      <dt>From</dt>
                      <dd>
                        {item.fromAmountMinor === undefined
                          ? "Price confirmed later"
                          : formatMoney(item.fromAmountMinor, item.currency)}
                      </dd>
                    </dl>
                    <button
                      className="button primary"
                      type="button"
                      onClick={() => chooseOffering(item)}
                    >
                      Check availability
                    </button>
                  </Card>
                ))}
              </div>
            ) : (
              <EmptyState
                title="Online booking is not configured"
                description="Contact the organization directly for venue inquiries."
              />
            )}
          </>
        ) : null}
        {offering && !requestToken && !result ? (
          <div className="stack">
            <Card>
              <CardHeader
                title={offering.details.headline}
                description={`${offering.venueName} · ${offering.locationName}`}
                actions={
                  <button
                    className="button ghost small"
                    type="button"
                    onClick={() => setOffering(null)}
                  >
                    Change space
                  </button>
                }
              />
              <form className="form-grid" onSubmit={findAvailability}>
                <Field label="Starting date">
                  <input
                    type="date"
                    value={date}
                    onChange={(event) => setDate(event.target.value)}
                    required
                  />
                </Field>
                <Field label="Duration (minutes)">
                  <input
                    type="number"
                    value={duration}
                    min={offering.details.minimumDurationMinutes}
                    max={offering.details.maximumDurationMinutes}
                    step={offering.details.slotIntervalMinutes}
                    onChange={(event) => setDuration(Number(event.target.value))}
                    required
                  />
                </Field>
                <Field label="Attendees">
                  <input
                    type="number"
                    value={attendees}
                    min={1}
                    max={offering.capacity}
                    onChange={(event) => setAttendees(Number(event.target.value))}
                    required
                  />
                </Field>
                <div className="full">
                  <FormActions submitting={working} submitLabel="Find available times" />
                </div>
              </form>
            </Card>
            {slots.length ? (
              <Card>
                <CardHeader
                  title="Available times"
                  description="We’ll hold your chosen time briefly while you enter your details."
                />
                <div className="slot-grid">
                  {slots.map((slot) => (
                    <button
                      className="slot"
                      type="button"
                      key={slot.start}
                      disabled={working}
                      onClick={() => createHold(slot)}
                    >
                      <strong>{formatDateTime(slot.start, offering.timezone)}</strong>
                      <small>
                        {slot.amountMinor === undefined
                          ? "Price confirmed later"
                          : formatMoney(slot.amountMinor, slot.currency)}
                      </small>
                    </button>
                  ))}
                </div>
              </Card>
            ) : null}
          </div>
        ) : null}
        {offering && requestToken && (!result || result.state === "held") ? (
          <Card>
            <CardHeader
              title="Your time is held"
              description={
                hold
                  ? `Complete this request before ${formatDateTime(hold.expiresAt, offering.timezone)}.`
                  : "Complete the request before the secure hold expires."
              }
            />{" "}
            <form className="form-grid" onSubmit={submit}>
              <Field label="Event title">
                <input name="title" required />
              </Field>
              <Field label="Contact name">
                <input name="contactName" autoComplete="name" required />
              </Field>
              <Field label="Email">
                <input name="contactEmail" type="email" autoComplete="email" required />
              </Field>
              <Field label="Phone">
                <input name="contactPhone" type="tel" autoComplete="tel" />
              </Field>
              {offering.details.questions.map((question) =>
                question.kind === "textarea" ? (
                  <Field key={question.key} label={question.label}>
                    <textarea name={question.key} required={question.required} />
                  </Field>
                ) : question.kind === "choice" ? (
                  <Field key={question.key} label={question.label}>
                    <select name={question.key} required={question.required} defaultValue="">
                      <option value="">Select</option>
                      {question.options?.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : question.kind === "boolean" ? (
                  <label className="check-field full" key={question.key}>
                    <input name={question.key} type="checkbox" required={question.required} />{" "}
                    {question.label}
                  </label>
                ) : (
                  <Field key={question.key} label={question.label}>
                    <input name={question.key} required={question.required} />
                  </Field>
                ),
              )}
              <label className="check-field full">
                <input name="privacyAccepted" type="checkbox" required /> I have read the privacy
                notice {offering.privacyNoticeVersion}.
              </label>
              <label className="check-field full">
                <input name="termsAccepted" type="checkbox" required /> I accept the booking terms{" "}
                {offering.termsVersion} and cancellation policy {offering.cancellationPolicyVersion}
                .
              </label>
              <label className="check-field full">
                <input name="marketingEmailConsent" type="checkbox" /> Send me occasional news and
                offers. This is optional and does not affect my booking.
              </label>
              <div className="full">
                <FormActions
                  submitting={working}
                  submitLabel={offering.mode === "automatic" ? "Book space" : "Submit request"}
                />
              </div>
            </form>
          </Card>
        ) : null}
        {result && result.state !== "held" ? (
          <Card>
            <CardHeader
              title={result.paymentRequired ? "Payment required" : "Request received"}
              actions={<StatusPill value={result.bookingStatus || result.state} />}
            />
            <div className="stack">
              <Notice tone="success">
                <span>
                  {result.bookingReference ? `Reference ${result.bookingReference}. ` : ""}
                  {result.paymentRequired
                    ? "Complete the secure payment step to continue."
                    : "The venue team has received your request."}
                </span>
              </Notice>
              {result.paymentRequired && result.paymentUrl ? (
                <a className="button primary" href={result.paymentUrl}>
                  Continue to secure payment
                </a>
              ) : null}
              {result.paymentRequired && !result.paymentUrl ? (
                <button
                  className="button primary"
                  type="button"
                  disabled={working}
                  onClick={retryPayment}
                >
                  Create a new payment link
                </button>
              ) : null}
              <button
                className="button secondary"
                type="button"
                onClick={() => {
                  sessionStorage.removeItem(storageKey);
                  setOffering(null);
                  setRequestToken("");
                  setResult(null);
                  setHold(null);
                  setSlots([]);
                }}
              >
                Start another request
              </button>
            </div>
          </Card>
        ) : null}
      </main>
      <footer className="public-footer">
        <small>
          Your details are protected and only used to manage this booking. The venue is responsible
          for how it uses them; the platform's own <Link to="/legal/privacy">privacy notice</Link>{" "}
          explains its part.
        </small>
      </footer>
    </div>
  );
}
